import { Info } from "lucide-react";
import { useState } from "react";

interface TicketContextSectionProps {
  /** The ticket's `context` field — arbitrary key/value metadata */
  context: Record<string, unknown> | null | undefined;
}

/** Ordered scalar fields rendered as key/value pairs (mockup `context-item`).
 *  Keys match the backend `context` JSONB shape (snake_case). */
const CONTEXT_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "http_error", label: "Erreur HTTP" },
  { key: "user_agent", label: "Navigateur" },
  { key: "screen_size", label: "Écran" },
];

/** Labels for each nested `affected_routes` entry. */
const AFFECTED_ROUTE_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "route", label: "Route" },
  { key: "route_title", label: "Page" },
  { key: "error_status", label: "Erreur HTTP" },
];

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function TicketContextSection({ context }: TicketContextSectionProps) {
  const [open, setOpen] = useState(false);

  if (!context || Object.keys(context).length === 0) return null;

  const affectedRoutes = Array.isArray(context.affected_routes)
    ? (context.affected_routes as Record<string, unknown>[])
    : [];

  const scalarFields = CONTEXT_FIELDS.filter(({ key }) => {
    const value = context[key];
    return value !== null && value !== undefined && value !== "";
  });

  const hasContent = scalarFields.length > 0 || affectedRoutes.length > 0;
  if (!hasContent) return null;

  return (
    <div className="collapse collapse-arrow bg-base-200 border border-base-300/70 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200">
      <input
        type="checkbox"
        className="peer"
        checked={open}
        onChange={() => setOpen(!open)}
        aria-label="Afficher le contexte"
      />
      <div className="collapse-title flex items-center gap-2 py-3">
        <Info className="w-4 h-4 text-base-content/50" />
        <h3 className="card-title text-sm font-semibold">Contexte</h3>
      </div>
      <div className="collapse-content">
        <div className="space-y-4">
          {/* Scalar key/value pairs — 2-column grid */}
          {scalarFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-base-300 rounded-lg overflow-hidden text-sm">
              {scalarFields.map(({ key, label }) => (
                <div key={key} className="bg-base-200 p-3 space-y-1">
                  <span className="text-xs font-medium text-base-content/50">{label}</span>
                  <p className="font-medium break-all">{renderValue(context[key])}</p>
                </div>
              ))}
            </div>
          )}

          {/* Nested affected_routes — each route as its own row group */}
          {affectedRoutes.length > 0 && (
            <div className="space-y-3">
              {affectedRoutes.map((route, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-base-300 rounded-lg overflow-hidden text-sm"
                >
                  {AFFECTED_ROUTE_FIELDS.map(({ key, label }) => (
                    <div key={key} className="bg-base-200 p-3 space-y-1">
                      <span className="text-xs font-medium text-base-content/50">{label}</span>
                      <p className="font-medium break-all">{renderValue(route[key])}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
